import type { SubmitHandler } from "react-hook-form"
import { object, string, type TestContext } from "yup"

import { LeadPreferredMethod } from "~graphql/generated/graphql"
import COPY from "~config/copy-constants"
import type {
    UserFieldsFragment,
    LeadInput,
    PublicListingFieldsFragment,
    ListingFieldsFragment,
} from "~graphql/generated/graphql"
import { getDateRange } from "~utils/date-helpers"
import { useCreateLead } from "~utils/use-create-lead"

const VALIDATION_NAMES = {
    END_DATE_MUST_BE_AFTER_START_DATE: "is-after-start-date",
}

export const REQUEST_QUOTE_FORM_KEYS = {
    FIRST_NAME: "first_name",
    LAST_NAME: "last_name",
    EMAIL: "email",
    PHONE: "phone",
    PREFERRED_CONTACT: "preferred_contact",
    LISTING_ID: "listing_id",
    PREFERRED_START_DATE: "preferred_start_date",
    PREFERRED_END_DATE: "preferred_end_date",
    TRIP_DETAILS: "trip_details",
} as const

interface RequestQuoteFormValues {
    [REQUEST_QUOTE_FORM_KEYS.FIRST_NAME]: string
    [REQUEST_QUOTE_FORM_KEYS.LAST_NAME]: string
    [REQUEST_QUOTE_FORM_KEYS.EMAIL]: string
    [REQUEST_QUOTE_FORM_KEYS.PHONE]?: string
    [REQUEST_QUOTE_FORM_KEYS.PREFERRED_CONTACT]: LeadPreferredMethod
    [REQUEST_QUOTE_FORM_KEYS.LISTING_ID]: string
    [REQUEST_QUOTE_FORM_KEYS.PREFERRED_START_DATE]: string
    [REQUEST_QUOTE_FORM_KEYS.PREFERRED_END_DATE]: string
    [REQUEST_QUOTE_FORM_KEYS.TRIP_DETAILS]: string
}

export function getInitialRequestQuoteFormValues({
    listing,
    user,
}: {
    listing?: PublicListingFieldsFragment | ListingFieldsFragment
    user?: UserFieldsFragment | null
}): Partial<RequestQuoteFormValues> {
    return {
        [REQUEST_QUOTE_FORM_KEYS.FIRST_NAME]: user?.first_name ?? undefined,
        [REQUEST_QUOTE_FORM_KEYS.LAST_NAME]: user?.last_name ?? undefined,
        [REQUEST_QUOTE_FORM_KEYS.EMAIL]: user?.email ?? undefined,
        [REQUEST_QUOTE_FORM_KEYS.PHONE]: user?.phone ?? undefined,
        [REQUEST_QUOTE_FORM_KEYS.PREFERRED_CONTACT]: LeadPreferredMethod.Email,
        [REQUEST_QUOTE_FORM_KEYS.LISTING_ID]: listing?.id ?? undefined,
        [REQUEST_QUOTE_FORM_KEYS.PREFERRED_START_DATE]: undefined,
        [REQUEST_QUOTE_FORM_KEYS.PREFERRED_END_DATE]: undefined,
        [REQUEST_QUOTE_FORM_KEYS.TRIP_DETAILS]: undefined,
    }
}

export function isEndDateAfterStartDate(
    this: TestContext<Partial<RequestQuoteFormValues>>,
    value: string | undefined | null // Yup passes value as undefined/null if not present
): boolean {
    const startDateString =
        this.parent[REQUEST_QUOTE_FORM_KEYS.PREFERRED_START_DATE]
    // Allow validation to pass if either value is missing; 'required' will catch this
    if (!value || !startDateString) {
        return true
    }

    const startDate = new Date(startDateString)
    const endDate = new Date(value)

    // Check if dates are valid before comparing
    if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
        // Let other validations handle invalid date formats if needed
        return true
    }

    return endDate > startDate
}

export function getRequestQuoteFormSchema() {
    return object({
        [REQUEST_QUOTE_FORM_KEYS.FIRST_NAME]: string().min(1).required(),
        [REQUEST_QUOTE_FORM_KEYS.LAST_NAME]: string().min(1).required(),
        [REQUEST_QUOTE_FORM_KEYS.EMAIL]: string().email().required(),
        [REQUEST_QUOTE_FORM_KEYS.PHONE]: string().optional(),
        [REQUEST_QUOTE_FORM_KEYS.PREFERRED_CONTACT]: string()
            .oneOf(Object.values(LeadPreferredMethod))
            .required(),
        [REQUEST_QUOTE_FORM_KEYS.LISTING_ID]: string().required(),
        [REQUEST_QUOTE_FORM_KEYS.PREFERRED_START_DATE]: string().required(),
        [REQUEST_QUOTE_FORM_KEYS.PREFERRED_END_DATE]: string().required().test({
            name: VALIDATION_NAMES.END_DATE_MUST_BE_AFTER_START_DATE,
            message:
                COPY.REQUEST_QUOTE_FORM_ERRORS
                    .END_DATE_MUST_BE_AFTER_START_DATE,
            test: isEndDateAfterStartDate,
        }),
        [REQUEST_QUOTE_FORM_KEYS.TRIP_DETAILS]: string().required(),
    })
}

export const RADIO_OPTIONS = Object.values(LeadPreferredMethod).map(
    (method) => ({
        value: method,
        label: method,
    })
)

function getCreateLeadInput(values: RequestQuoteFormValues): LeadInput {
    return {
        contact: {
            first_name: values[REQUEST_QUOTE_FORM_KEYS.FIRST_NAME],
            last_name: values[REQUEST_QUOTE_FORM_KEYS.LAST_NAME],
            email: values[REQUEST_QUOTE_FORM_KEYS.EMAIL],
            preferred_method: values[REQUEST_QUOTE_FORM_KEYS.PREFERRED_CONTACT],
            ...(values[REQUEST_QUOTE_FORM_KEYS.PHONE] && {
                phone: values[REQUEST_QUOTE_FORM_KEYS.PHONE],
            }),
        },
        listing_id: values[REQUEST_QUOTE_FORM_KEYS.LISTING_ID],
        dates: getDateRange(
            values[REQUEST_QUOTE_FORM_KEYS.PREFERRED_START_DATE],
            values[REQUEST_QUOTE_FORM_KEYS.PREFERRED_END_DATE]
        ),
        trip_details: values[REQUEST_QUOTE_FORM_KEYS.TRIP_DETAILS],
    }
}

export function useHandleFormSubmit({ onSuccess }: { onSuccess?: () => void }) {
    const { createLead, isLoading } = useCreateLead({
        onSuccess,
    })

    const handleSubmit: SubmitHandler<RequestQuoteFormValues> = (formData) => {
        const input = getCreateLeadInput(formData)

        return createLead(input)
    }

    return { handleSubmit, isLoadingSubmit: isLoading }
}
